import {
  Badge,
  Collapse,
  Filter,
  Timeline,
  ViewPlaceholder,
} from '@vrplatform/components';
import {
  useDashboard,
  useMutation,
  useTeam,
  useTeamRole,
} from '@vrplatform/data';
import {
  CalendarDatesIcon,
  CalendarEventIcon,
  CheckCircleIcon,
  CrossCircleIcon,
  CrossIcon,
  Edit3Icon,
  HomeIcon,
  Icon,
  LoaderIcon,
  RocketIcon,
  UserIcon,
} from '@vrplatform/icons';
import { LazyTable, type MRT_ColumnDef } from '@vrplatform/table';
import {
  Drawer,
  EllipsisMenuCopyItem,
  EllipsisMenuItem,
} from '@vrplatform/components-legacy';
import {
  type Maybe,
  formatCurrency,
  groupBy,
  hasValue,
  sum,
  toTitleCase,
  uniqueBy,
  utc,
} from '@vrplatform/utils';
import {
  Alert,
  Anchor,
  Avatar,
  Box,
  Card,
  Center,
  Group,
  LoadingOverlay,
  Progress,
  Stack,
  Tabs,
  Text,
  Tooltip,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { XIMPLIFI_TENANT_ID } from '@vrplatform/ui-common';
import { useMemo, useState } from 'react';
import {
  EllipsisMenuSourceDownloadItem,
  HiddenFeatureIndicator,
  ReservationStatusBadge,
} from '../../components';
import {
  useAutomationHistoryDrawer,
  useAutomationModal,
  useFileDownload,
} from '../../hooks';
import { SelectPostAutomationModal } from '../../modals';
import {
  DrawerCollapsableTable,
  DrawerHeader,
  DrawerInfoCard,
} from '../_components';
import {
  ReservationEditForm,
  ReservationRefetchEllipsisItem,
} from './_components';
import { useHasReservationUserDataEditAccess } from './_hooks';
import {
  type LegacyReservation,
  useLegacyReservationDetailDrawerQuery,
} from './_queries';
import { useReservationDetailDrawer } from './useReservationDetailDrawer';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import { useListingDetailDrawer } from '../../views/listings/drawer';
import { usePaymentDetailDrawer } from '../payment-drawers';

export const LegacyReservationDetailDrawer = () => {
  const { opened, close, reservationId, view, setView } =
    useReservationDetailDrawer();

  const { colorScheme } = useMantineColorScheme();

  const { colors } = useMantineTheme();
  const { isLoading, data: reservation } =
    useLegacyReservationDetailDrawerQuery(reservationId);

  const {
    hasAutomations,
    automations,
    handlers: automationModalHandlers,
    opened: openedAutomationModal,
    postAutomations,
  } = useAutomationModal('finalytic.reservation');

  const updatePaymentData = useMutation(
    (
      q,
      { reservationId, userdata }: { reservationId: string; userdata: any }
    ) => {
      return q.updateReservation({
        pk_columns: {
          id: reservationId,
        },
        _set: {
          userdata,
        },
      })?.id;
    },
    {
      invalidateQueryKeys: ['reservations'],
    }
  );

  const hasUserDataEditAccess = useHasReservationUserDataEditAccess();

  return (
    <>
      <SelectPostAutomationModal
        opened={openedAutomationModal && !!reservationId}
        onClose={automationModalHandlers.close}
        onAutomationSubmit={(automationIds) =>
          postAutomations({
            allPagesSelected: false,
            automationIds,
            filterState: {},
            getSelectedRowIds: () => (reservationId ? [reservationId] : []),
            where: {},
            selectedItemType: 'finalytic.reservation',
            skipCachedActions: false,
            skipSelectionNeccesary: false,
          }).then(() => close())
        }
        type="finalytic.reservation"
        automations={automations}
      />
      <Drawer opened={opened} onClose={close} size={550}>
        <DrawerHeader
          closeDrawer={close}
          title={
            reservation && (
              <Group mt={rem(5)} wrap="nowrap">
                <Avatar src={reservation.connection.app.iconRound} />
                <Box>
                  <Text size="lg" fw={500}>
                    {reservation.guestName}
                  </Text>
                  <Text
                    size="xs"
                    c={colorScheme === 'dark' ? colors.gray[6] : 'gray'}
                    fw={400}
                  >
                    {`${uniqueBy(
                      [
                        reservation?.confirmationCode,
                        reservation?.pmsReferenceCode,
                      ].filter((x) => x)
                    ).join(', ')}`}
                  </Text>
                </Box>
              </Group>
            )
          }
          type="Reservation"
          loading={isLoading}
          menuItems={
            view === 'overview' &&
            (hasUserDataEditAccess || hasAutomations) &&
            reservation && (
              <>
                {hasAutomations && (
                  <EllipsisMenuItem
                    onClick={automationModalHandlers.open}
                    customIcon={<RocketIcon size={16} />}
                  >
                    Post Automation
                  </EllipsisMenuItem>
                )}
                {hasUserDataEditAccess && (
                  <EllipsisMenuItem
                    onClick={() => setView('edit')}
                    customIcon={<Edit3Icon size={16} />}
                  >
                    Edit User Data
                  </EllipsisMenuItem>
                )}

                {reservation.connection?.id && (
                  <ReservationRefetchEllipsisItem
                    appId={reservation.connection.app.id}
                    connectionId={reservation.connection.id}
                    uniqueRef={reservation.uniqueRef}
                    refreshType="show-notification"
                  />
                )}

                <HiddenFeatureIndicator permission="vrp-admin">
                  <EllipsisMenuCopyItem value={reservation.id} />

                  <EllipsisMenuSourceDownloadItem
                    fileName={reservation.confirmationCode || reservation.id}
                    where={{ reservation: { id: { _eq: reservation.id } } }}
                  />
                </HiddenFeatureIndicator>
              </>
            )
          }
        />
        {!reservation && !isLoading ? (
          <ViewPlaceholder
            title="No reservation found"
            description="Has the reservation been deleted?"
            icon="CommentQuestionIcon"
            backgroundColor="transparent"
          />
        ) : view === 'edit' && reservation ? (
          <ReservationEditForm
            initialValues={{
              userData: JSON.stringify(reservation.userData || {}, null, 2),
            }}
            onReset={() => setView('overview')}
            isLoading={isLoading}
            handleSubmit={async (values) => {
              await updatePaymentData
                .mutate({
                  args: {
                    reservationId: reservation.id,
                    userdata: JSON.parse(values.userData?.trim() || '{}'),
                  },
                })
                .then(() => setView('overview'));
            }}
          />
        ) : (
          <ReservationDetail reservation={reservation} isLoading={isLoading} />
        )}
      </Drawer>
    </>
  );
};

const ReservationDetail = ({
  reservation,
  isLoading,
}: {
  reservation: Maybe<LegacyReservation>;
  isLoading: boolean;
}) => {
  const tabs = useMemo(() => {
    const tabs: (
      | 'financials'
      | 'excluded'
      | 'payments'
      | 'paymentFeesAndReserves'
      | 'journalEntries'
    )[] = [];

    if (!reservation) return [];

    const limit = 10;

    if (
      Object.values(
        groupBy(reservation.paymentLines, (x) => x.paymentId || '')
      ).some((x) => x.length > limit)
    )
      tabs.push('payments');

    if (
      Object.values(
        groupBy(reservation.resolutionLines, (x) => x.paymentId || '')
      ).some((x) => x.length > limit)
    )
      tabs.push('paymentFeesAndReserves');

    if (reservation.financials.included.length > limit) tabs.push('financials');
    if (reservation.financials.exluded.length > limit) tabs.push('excluded');

    return tabs;
  }, [reservation]);

  if (!reservation) return null;

  // 2 financials not grouped
  // payments/resolutions grouped

  const showTabs = !!tabs.length;

  return (
    <Stack
      gap={'md'}
      mb="md"
      sx={{
        position: 'relative',
        flex: 1,
      }}
    >
      {!showTabs ? (
        <Overview reservation={reservation} />
      ) : (
        <Tabs
          defaultValue="overview"
          styles={(theme) => ({
            root: {
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              flex: 1,
            },
            panel: {
              height: '100%',
              flex: 1,
              marginTop: theme.spacing.md,
              maxWidth: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'calc(100vh - 140px)',
            },
          })}
        >
          <Tabs.List>
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            {tabs.map((key) => {
              return (
                <Tabs.Tab key={key} value={key}>
                  {toTitleCase(key)}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>

          <Tabs.Panel value="overview">
            <Stack gap={'md'} pb="md">
              <Alert icon={<Icon icon="AlertCircleIcon" size={20} />}>
                <Text size="sm">
                  This reservation contains more data than usual. You can use
                  tabs to view the data more easily.
                </Text>
              </Alert>
              <Overview reservation={reservation} />
            </Stack>
          </Tabs.Panel>
          {tabs.map((key) => {
            return (
              <Tabs.Panel key={key} value={key}>
                {key === 'payments' && (
                  <Payments
                    currency={reservation.currency}
                    rowData={reservation.paymentLines}
                    title="Payments"
                    type="tab"
                  />
                )}
                {key === 'paymentFeesAndReserves' && (
                  <Payments
                    currency={reservation.currency}
                    rowData={reservation.resolutionLines}
                    title="Payments"
                    type="tab"
                  />
                )}

                {key === 'financials' && (
                  <Financials
                    currency={reservation.currency}
                    rowData={reservation.financials.included}
                    reservationId={reservation.id}
                    title="Payments"
                    type="tab"
                  />
                )}

                {key === 'excluded' && (
                  <Financials
                    currency={reservation.currency}
                    rowData={reservation.financials.exluded}
                    reservationId={reservation.id}
                    title="Payments"
                    type="tab"
                  />
                )}
              </Tabs.Panel>
            );
          })}
        </Tabs>
      )}
      <LoadingOverlay
        visible={isLoading}
        loaderProps={{
          size: 'sm',
        }}
      />
    </Stack>
  );
};

const Overview = ({ reservation }: { reservation: LegacyReservation }) => {
  const { open: openListingDetailDrawer } = useListingDetailDrawer();

  const [, setView] = useQueryState('view', parseAsString);

  const userDataKeys = Object.keys(reservation.userData || {});

  return (
    <>
      <DrawerInfoCard
        rows={[
          {
            icon: LoaderIcon,
            title: 'Status',
            text: <ReservationStatusBadge status={reservation.status} />,
          },

          {
            icon: HomeIcon,
            title: 'Listing',
            text: reservation.listing.id && (
              <Anchor
                onClick={() => openListingDetailDrawer(reservation.listing.id!)}
              >
                {reservation.listing.name}
              </Anchor>
            ),
          },
          {
            icon: CalendarEventIcon,
            title: 'Booked',
            text: `${
              reservation.bookedAt
                ? utc(reservation.bookedAt).format('MMM DD, YYYY')
                : 'unknown date'
            }, ${toTitleCase(reservation.channel || 'unknown channel')}`,
          },
          {
            icon: CrossCircleIcon,
            title: 'Cancelled At',
            text:
              reservation.status === 'cancelled' && reservation.cancelledAt
                ? utc(reservation.cancelledAt).format('MMM DD, YYYY')
                : undefined,
          },
          {
            icon: CalendarDatesIcon,
            title: 'Dates',
            text: [reservation.checkIn, reservation.checkOut]
              .filter(hasValue)
              .map((x) => utc(x).format('MMM DD, YYYY'))
              .join(' - '),
          },
          {
            icon: UserIcon,
            title: 'User data',
            text: !!userDataKeys.length && (
              <Anchor onClick={() => setView('edit')}>
                {userDataKeys.length} updated key
                {userDataKeys.length > 1 && 's'}
              </Anchor>
            ),
          },
        ]}
      />

      <PaymentProgress
        currency={reservation.currency}
        financials={reservation.financials.included}
        paymentLines={reservation.paymentLines}
      />

      <Payments
        currency={reservation.currency}
        rowData={reservation.paymentLines}
        title="Payments"
      />

      {!!reservation.resolutionLines.length && (
        <Payments
          currency={reservation.currency}
          rowData={reservation.resolutionLines}
          title="Payment Fees & Reserves"
        />
      )}

      <Financials
        title="Financials"
        currency={reservation.currency}
        rowData={reservation.financials.included}
        reservationId={reservation.id}
      />

      {!!reservation.financials.exluded.length && (
        <Financials
          title="Financials - Excluded items"
          currency={reservation.currency}
          rowData={reservation.financials.exluded}
          reservationId={reservation.id}
        />
      )}

      <ReservationTimeline data={reservation.timeline} />

      {!!reservation.files.length && <Files rowData={reservation.files} />}
    </>
  );
};

const Financials = ({
  rowData,
  currency,
  title,
  type = 'collapse',
}: {
  rowData: LegacyReservation['financials']['included'];
  currency: string;
  title: string;
  reservationId: string;
  type?: 'tab' | 'collapse';
}) => {
  const [search, setSearch] = useState('');

  const [{ partnerId }] = useTeam();
  const { isSuperAdmin } = useTeamRole();

  const { mutate: upsertSettingMutation, loading: loadingSettingUpsert } =
    useMutation(
      (
        q,
        args: {
          settingId: Maybe<string>;
          type2: string;
          tenantId: string;
          value: 'excluded' | 'invoice';
          isLocalAutomationSetting: boolean;
        }
      ) => {
        if (args.isLocalAutomationSetting && args.settingId) {
          return q.insert_setting_one({
            object: {
              id: args.settingId,
              tenant_id: args.tenantId,
              value: args.value === 'invoice' ? 'invoice' : 'exclude',
              group: 'ximplifi',
              key: 'inclusion',
              target: args.type2,
              lineClassification: args.type2,
              leftType: 'finalytic.lineType',
            },
            on_conflict: {
              constraint: 'setting_pkey',
              update_columns: ['value'],
            },
          })?.id;
        }

        return q.insert_setting_one({
          object: {
            id: args.settingId,
            key: 'accountingType',
            group: 'ximplifi',
            partner: 'ximplifi',
            leftType: 'finalytic.lineType',
            tenant_id: args.tenantId,
            value: args.value,
            target: args.type2,
            lineClassification: args.type2,
          },
          on_conflict: {
            constraint: 'setting_pkey',
            update_columns: ['value'],
          },
        })?.id;
      },
      {
        invalidateQueryKeys: ['reservations'],
      }
    );

  const total = sum(rowData, (x) => x.centTotal || 0) / 100;

  const columns = useMemo<
    MRT_ColumnDef<LegacyReservation['financials']['included'][0]>[]
  >(
    () => [
      {
        header: 'Description',
        accessorKey: 'description',
        Cell: ({ row }) => {
          const data = row.original;

          return (
            <Tooltip
              label={
                <>
                  {toTitleCase(data.type2)}
                  {data?.lineItemSetting &&
                    (data?.lineItemSetting?.key === 'inclusion' ? (
                      <>
                        <br />
                        (via team)
                      </>
                    ) : (
                      <>
                        <br />
                        (via partner)
                      </>
                    ))}
                </>
              }
              withArrow
              withinPortal
            >
              <Text size="sm">{data.description}</Text>
            </Tooltip>
          );
        },
      },
      {
        header: 'amount',
        accessorKey: 'amount',
        maxSize: 80,
        mantineTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => {
          const amount = formatCurrency(
            (row.original.centTotal || 0) / 100,
            currency
          );

          return amount;
        },
      },
    ],
    [currency]
  );

  const trimmed = search.trim();

  const filtered = useMemo(() => {
    if (!trimmed) return rowData;

    return rowData.filter((row) => {
      return row.description?.toLowerCase().includes(trimmed.toLowerCase());
    });
  }, [trimmed, rowData]);

  if (type === 'tab') {
    return (
      <LazyTable
        data={{
          loading: false,
          error: false,
          rowCount: filtered.length,
          rows: filtered,
        }}
        table={{
          columns,
          // hideTopBar: true,
          hideSettings: true,
          hideHeader: true,
          hidePagination: true,
          emptyRowsFallback: () => (
            <Center>
              <Text size="sm" c="gray">
                No financials available
              </Text>
            </Center>
          ),
        }}
        rowMenu={
          !isSuperAdmin
            ? undefined
            : {
                menuItems: ({ row }) => {
                  const isXimplifiPartner = partnerId === XIMPLIFI_TENANT_ID;

                  if (!row.original.type2 || !isXimplifiPartner) return null;

                  const isExcluded = row.original.isInvoice === 'excluded';

                  const lineItemSetting = row.original.lineItemSetting;

                  const isLocalAutomationSetting =
                    lineItemSetting?.key === 'inclusion';

                  return (
                    <HiddenFeatureIndicator permission="super-admin">
                      <EllipsisMenuItem
                        customIcon={<CrossIcon size={16} />}
                        loading={loadingSettingUpsert}
                        onClick={() => {
                          upsertSettingMutation({
                            args: {
                              settingId: lineItemSetting?.id,
                              type2: row.original.type2!,
                              tenantId: partnerId,
                              value: isExcluded ? 'invoice' : 'excluded',
                              isLocalAutomationSetting,
                            },
                          });
                        }}
                      >
                        {isExcluded ? 'Include' : 'Exclude'} item
                      </EllipsisMenuItem>
                    </HiddenFeatureIndicator>
                  );
                },
              }
        }
        resetFilter={() => setSearch('')}
      >
        <Filter.Search setValue={setSearch} value={search} />
      </LazyTable>
    );
  }

  return (
    <Box>
      <DrawerCollapsableTable
        title={title}
        rightSection={
          <Text size="sm" fw={500}>
            {formatCurrency(total, currency)}
          </Text>
        }
        rowMenu={
          !isSuperAdmin
            ? undefined
            : {
                menuItems: ({ row }) => {
                  const isXimplifiPartner = partnerId === XIMPLIFI_TENANT_ID;

                  if (!row.original.type2 || !isXimplifiPartner) return null;

                  const isExcluded = row.original.isInvoice === 'excluded';

                  const lineItemSetting = row.original.lineItemSetting;

                  const isLocalAutomationSetting =
                    lineItemSetting?.key === 'inclusion';

                  return (
                    <HiddenFeatureIndicator permission="super-admin">
                      <EllipsisMenuItem
                        customIcon={<CrossIcon size={16} />}
                        loading={loadingSettingUpsert}
                        onClick={() => {
                          upsertSettingMutation({
                            args: {
                              settingId: lineItemSetting?.id,
                              type2: row.original.type2!,
                              tenantId: partnerId,
                              value: isExcluded ? 'invoice' : 'excluded',
                              isLocalAutomationSetting,
                            },
                          });
                        }}
                      >
                        {isExcluded ? 'Include' : 'Exclude'} item
                      </EllipsisMenuItem>
                    </HiddenFeatureIndicator>
                  );
                },
              }
        }
        rowData={rowData}
        columns={columns}
        emptyRowsFallback={() => (
          <Center>
            <Text size="sm" c="gray">
              No financials available
            </Text>
          </Center>
        )}
      />
    </Box>
  );
};

const Files = ({ rowData }: { rowData: LegacyReservation['files'] }) => {
  const { download } = useFileDownload();

  const [dashboard] = useDashboard();

  return (
    <DrawerCollapsableTable
      title={'Files'}
      rightSection={null}
      rowData={rowData}
      columns={[
        {
          header: 'Name',
          accessorKey: 'filename',
        },
        dashboard !== 'owner'
          ? {
              header: 'Owner',
              accessorKey: 'ownerName',
            }
          : undefined,
        {
          header: 'startDate',
          accessorKey: 'startDate',
        },
      ].filter(hasValue)}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" color="gray">
            No files found
          </Text>
        </Center>
      )}
      onRowClick={{
        handler: (row) => {
          download([
            {
              fileId: row.original.id,
              filename: row.original.filename,
            },
          ]);
        },
      }}
    />
  );
};

const PaymentProgress = ({
  financials,
  paymentLines,
  currency,
}: {
  paymentLines: LegacyReservation['paymentLines'];
  financials: LegacyReservation['financials']['included'];
  currency: string;
}) => {
  const totalFinancials = sum(financials, (x) => x.centTotal || 0);
  const totalPayments = sum(paymentLines, (x) => x.centTotal || 0);
  const totalPending = totalFinancials - totalPayments;

  const { colors } = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  return (
    <Card
      sx={(theme) => ({
        backgroundColor:
          colorScheme === 'dark' ? undefined : theme.colors.neutral[1],
        display: 'flex',
        flexDirection: 'column',
        gap: rem(14),
      })}
      radius="md"
      mb="sm"
    >
      <Group justify="space-between">
        <Amount
          align="left"
          centTotal={totalPayments}
          currency={currency}
          label="Paid"
        />

        {totalPending ? (
          <Amount
            align="center"
            centTotal={totalPending}
            currency={currency}
            label="Pending"
            isPending
          />
        ) : (
          <Center sx={{ flexDirection: 'column' }}>
            <CheckCircleIcon size={24} color={colors.green[7]} mb={8} />
            <Badge color="green">All Paid</Badge>
          </Center>
        )}

        <Amount
          align="right"
          centTotal={totalFinancials}
          currency={currency}
          label="Expected"
        />
      </Group>

      <Progress
        value={(totalPayments / totalFinancials) * 100}
        color={!totalPending ? 'green' : undefined}
      />
    </Card>
  );
};

const Amount = ({
  align,
  centTotal,
  currency,
  isPending,
  label,
}: {
  align: 'left' | 'center' | 'right';
  centTotal: number;
  currency: string;
  label: string;
  isPending?: boolean;
}) => {
  const amount = formatCurrency(centTotal / 100, currency);
  const { colors } = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  return (
    <Box>
      <Text
        ta={align}
        size="sm"
        c={colorScheme === 'dark' ? undefined : isPending ? 'gray' : 'neutral'}
      >
        {label}
      </Text>
      <Text
        ta={align}
        c={
          isPending
            ? colorScheme === 'dark'
              ? colors.gray[6]
              : 'gray'
            : undefined
        }
        fw={500}
        size="lg"
      >
        {amount}
      </Text>
    </Box>
  );
};

const Payments = ({
  rowData,
  currency,
  title,
  type = 'collapse',
}: {
  rowData: LegacyReservation['paymentLines'];
  currency: string;
  title: string;
  type?: 'tab' | 'collapse';
}) => {
  const [search, setSearch] = useState('');

  const { open: openPaymentDetailDrawer } = usePaymentDetailDrawer();

  const total = sum(rowData, (x) => x.centTotal || 0) / 100;

  type PaymentRow = {
    id: string;
    description: string;
    type: Maybe<LegacyReservation['paymentLines'][0]['type']>;
    centTotal: number;
    isParent: boolean;
    lines: PaymentRow[];
  };

  const groupedRowData = useMemo(
    () =>
      Object.entries(
        groupBy(rowData, (x) => x.paymentId || '-')
      ).map<PaymentRow>(([paymentId, paymentLines]) => {
        const firstPaymentLine = paymentLines[0];
        const payedAt = firstPaymentLine?.payment?.payedAt || '';

        const description = firstPaymentLine?.payment?.description || 'Payment';

        return {
          id: paymentId,
          isParent: true,
          type: firstPaymentLine?.type,
          centTotal: sum(paymentLines, (x) => x.centTotal || 0),
          description: [
            toTitleCase(description),
            payedAt ? `(${utc(payedAt).format('DD MMM YYYY')})` : '',
          ]
            .filter((i) => !!i)
            .join(' '),
          lines: paymentLines.map((paymentLine) => ({
            centTotal: paymentLine.centTotal || 0,
            description: paymentLine.description || '',
            lines: [],
            type: null,
            isParent: false,
            id: paymentLine.id,
          })),
        };
      }),
    [rowData]
  );

  const trimmed = search.trim();

  const filtered = useMemo(() => {
    if (!trimmed) return groupedRowData;

    return groupedRowData.filter((row) => {
      const line = row.description
        ?.toLowerCase()
        .includes(trimmed.toLowerCase());
      const childs = row.lines.some((x) =>
        x.description?.toLowerCase().includes(trimmed.toLowerCase())
      );

      return line || childs;
    });
  }, [trimmed, groupedRowData]);

  const columns = useMemo<MRT_ColumnDef<PaymentRow>[]>(
    () => [
      {
        header: 'Payed On',
        accessorKey: 'description',
      },
      {
        header: 'amount',
        accessorKey: 'centTotal',
        maxSize: 80,
        mantineTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => {
          return formatCurrency((row.original.centTotal || 0) / 100, currency);
        },
      },
    ],
    [currency]
  );

  if (type === 'tab') {
    return (
      <LazyTable
        data={{
          loading: false,
          error: false,
          rowCount: filtered.length,
          rows: filtered,
        }}
        table={{
          columns,
          hideSettings: true,
          hideHeader: true,
          hidePagination: true,
          onRowClick: {
            handler: (row) => {
              if (!row.original.isParent) return;
              openPaymentDetailDrawer(row.original.id);
            },
            disabled: (row) => !row.original.isParent,
          },
          emptyRowsFallback: () => (
            <Center>
              <Text size="sm" color="gray">
                No payments available
              </Text>
            </Center>
          ),
        }}
        subRows={{
          getRowCanExpand: (row) => !!row.original.lines.length,
          defaultExpanded: true,
          getSubRows: (row) => row.lines,
        }}
        resetFilter={() => setSearch('')}
      >
        <Filter.Search setValue={setSearch} value={search} />
      </LazyTable>
    );
  }

  return (
    <DrawerCollapsableTable
      title={title}
      rightSection={
        <Text size="sm" fw={500}>
          {formatCurrency(total, currency)}
        </Text>
      }
      rowData={groupedRowData}
      columns={columns}
      subRows={{
        getRowCanExpand: (row) => !!row.original.lines.length,
        defaultExpanded: true,
        getSubRows: (row) => row.lines,
      }}
      onRowClick={{
        handler: (row) => {
          if (!row.original.isParent) return;
          openPaymentDetailDrawer(row.original.id);
        },
        disabled: (row) => !row.original.isParent,
      }}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" c="gray">
            No payments available
          </Text>
        </Center>
      )}
    />
  );
};

const ReservationTimeline = ({
  data,
}: {
  data: LegacyReservation['timeline'];
}) => {
  const { colors } = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const { open } = useAutomationHistoryDrawer();
  const { isVrpAdmin } = useTeamRole();

  const items = useMemo(() => {
    return data.map((x) => ({
      ...x,
      onClick: x.syncId
        ? () =>
            open(x.syncId, {
              type: 'push',
            })
        : undefined,
      menuItems: isVrpAdmin ? (
        <EllipsisMenuCopyItem value={x.id}>Copy change ID</EllipsisMenuCopyItem>
      ) : undefined,
    }));
  }, [data, open, isVrpAdmin]);

  return (
    <Box>
      <Collapse
        title={
          <Text component="span" size="sm">
            Timeline
            <Text
              c={colorScheme === 'dark' ? colors.gray[6] : 'gray'}
              ml="xs"
              size="xs"
              component="span"
            >
              {items.length}
            </Text>
          </Text>
        }
        minHeight={30}
        defaultOpened={!!items.length}
      >
        <Timeline data={items} order="desc" />
      </Collapse>
    </Box>
  );
};
